import { useEffect, useState, useRef } from 'react';
import api from '../lib/api';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from './ui/carousel';

// V14.6 — Web banner carousel (parity with the App "Home (Top)" banners).
// Renders the active banners returned by GET /api/banners/active for the
// current learner's audience. Multiple banners => a real carousel (auto-advance
// + arrows + dots), never stacked. Image banners render as <img>; video banners
// render the SIGNED Bunny iframe embed (embed_url) — the only reliably-playable
// URL (direct CDN .m3u8 is 403 by library config).
export const BannerCarousel = ({ placement = 'home_top' }) => {
  const [banners, setBanners] = useState([]);
  const [api_, setApi_] = useState(null);
  const [current, setCurrent] = useState(0);
  const autoplayRef = useRef(null);

  useEffect(() => {
    let active = true;
    api.get(`/api/banners/active?placement=${placement}&platform=web`)
      .then((r) => { if (active) setBanners(r.data?.banners || []); })
      .catch(() => { if (active) setBanners([]); });
    return () => { active = false; };
  }, [placement]);

  // Track selected slide for the dots + auto-advance through embla api.
  useEffect(() => {
    if (!api_) return;
    const onSelect = () => setCurrent(api_.selectedScrollSnap());
    api_.on('select', onSelect);
    onSelect();
    return () => { api_.off('select', onSelect); };
  }, [api_]);

  useEffect(() => {
    if (!api_ || banners.length <= 1) return;
    autoplayRef.current = setInterval(() => {
      if (api_.canScrollNext()) api_.scrollNext();
      else api_.scrollTo(0);
    }, 6000);
    return () => clearInterval(autoplayRef.current);
  }, [api_, banners.length]);

  const trackClick = (id) => { if (id) api.post(`/api/banners/${id}/click`).catch(() => {}); };

  if (!banners.length) return null;

  return (
    <div className="relative" data-testid="banner-carousel">
      <Carousel opts={{ loop: banners.length > 1 }} setApi={setApi_} className="w-full">
        <CarouselContent>
          {banners.map((b) => (
            <CarouselItem key={b.id || b._id} data-testid="banner-slide">
              <BannerSlide banner={b} onClick={() => trackClick(b.id || b._id)} />
            </CarouselItem>
          ))}
        </CarouselContent>
        {banners.length > 1 && (
          <>
            <CarouselPrevious className="left-3 bg-white/80 backdrop-blur border-none shadow" data-testid="banner-prev" />
            <CarouselNext className="right-3 bg-white/80 backdrop-blur border-none shadow" data-testid="banner-next" />
          </>
        )}
      </Carousel>
      {banners.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-3" data-testid="banner-dots">
          {banners.map((b, i) => (
            <button
              key={b.id || b._id}
              onClick={() => api_ && api_.scrollTo(i)}
              data-testid={`banner-dot-${i}`}
              aria-label={`Go to banner ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${i === current ? 'w-6 bg-primary' : 'w-1.5 bg-muted-foreground/30'}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const BannerSlide = ({ banner, onClick }) => {
  const isVideo = banner.media_type === 'video';
  return (
    <div
      onClick={onClick}
      className="relative rounded-2xl overflow-hidden bg-muted aspect-[16/7] sm:aspect-[16/6] cursor-pointer"
      data-testid={`banner-${banner.media_type}-slide`}
    >
      {isVideo ? (
        (banner.video_kind === 'file' || (!banner.embed_url && banner.media_url)) ? (
          <video
            src={banner.media_url}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            poster={banner.thumbnail_url || undefined}
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="banner-video-file"
          />
        ) : banner.embed_url ? (
          <iframe
            src={`${banner.embed_url}${banner.embed_url.includes('?') ? '&' : '?'}autoplay=true&loop=true&muted=true&preload=true&responsive=true`}
            title={banner.title || 'banner'}
            loading="lazy"
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            className="absolute inset-0 w-full h-full border-0"
            data-testid="banner-video-iframe"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">Video unavailable</div>
        )
      ) : (
        <img
          src={banner.media_url || banner.image_url}
          alt={banner.title || 'banner'}
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="banner-image"
        />
      )}
      {(banner.title || banner.subtitle) && !isVideo && (
        <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5 bg-gradient-to-t from-black/60 via-black/25 to-transparent">
          {banner.title && <p className="text-white font-heading text-lg sm:text-xl font-semibold leading-tight drop-shadow">{banner.title}</p>}
          {banner.subtitle && <p className="text-white/90 text-sm mt-0.5 line-clamp-2 drop-shadow">{banner.subtitle}</p>}
        </div>
      )}
    </div>
  );
};
